import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { IThunkAPI, IUserState } from '../types';
import type {
  IUserProfile,
  IUserCredentials,
  IUserCredentialsRequest,
  IUserShipping,
  IUserIdentificationRequest,
  IUserChangePasswordRequest,
  IUserChangeShippingAddressRequest,
  IUserNotifyRequest,
  IUserNotifyTestPhoneNumberRequest,
  IApiErrorMessage,
  ITimeZoneName,
} from '@/api/types';
import { RootState } from '..';
import { user as api } from '@/api';
import { isFulfilledAction, isPendingAction, isRejectedAction, logError } from '@/helpers';
import { ICodeRequest, IHelpRequest, IMobileRequest } from '@/api/types';

const initialState: IUserState = {
  profile: null,
  shippings: [],
  credentials: [],
  timezonenames: [],
  isError: false,
  isSuccess: false,
  isFetching: false,
  declinedApplications: [],
  declinedMsg: '',
  declinedMsgHeading: '',
};

export const getUserProfile = createAsyncThunk<IUserProfile, undefined, IThunkAPI>(
  'pub/lxuser',
  async (_, thunkAPI) => {
    try {
      const response = await api.getUserProfile();
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getUserShippings = createAsyncThunk<IUserShipping[], undefined, IThunkAPI>(
  'agent/profile/shipping/get',
  async (_, thunkAPI) => {
    try {
      const response = await api.getUserProfileShippings();
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getTimeZoneNames = createAsyncThunk<ITimeZoneName[], undefined, IThunkAPI>(
  'agent/profile/timezonenames/get',
  async (_, thunkAPI) => {
    try {
      const response = await api.getTimeZoneNames();
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getUserCredentials = createAsyncThunk<IUserCredentials[], undefined, IThunkAPI>(
  'agent/profile/credentials/get',
  async (_, thunkAPI) => {
    try {
      const response = await api.getUserProfileCredentials();
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const setUserCredentials = createAsyncThunk<unknown, IUserCredentialsRequest, IThunkAPI>(
  'agent/profile/credentials/set',
  async (payload, thunkAPI) => {
    try {
      return api.setUserProfileCredentials(payload);
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const setUserIdentification = createAsyncThunk<IUserProfile, IUserIdentificationRequest, IThunkAPI>(
  'agent/profile/set/identification/set',
  async (payload, thunkAPI) => {
    try {
      const response = await api.setUserProfileIdentification(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const setUserNotify = createAsyncThunk<IUserProfile, IUserNotifyRequest, IThunkAPI>(
  'agent/profile/set/notify',
  async (payload, thunkAPI) => {
    try {
      const response = await api.setUserProfileNotify(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const setUserPassword = createAsyncThunk<IUserProfile, IUserChangePasswordRequest, IThunkAPI>(
  'agent/profile/set/password',
  async (payload, thunkAPI) => {
    try {
      const response = await api.setUserProfilePassword(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const setUserProfileShippingAddress = createAsyncThunk<
  IUserProfile | IApiErrorMessage[],
  IUserChangeShippingAddressRequest,
  IThunkAPI
>('agent/profile/set/address', async (payload, thunkAPI) => {
  try {
    const response = await api.setUserProfileShippingAddress(payload);
    return response.data;
  } catch (e) {
    logError(e);
    return thunkAPI.rejectWithValue(e.response);
  }
});

export const testNotifySms = createAsyncThunk<unknown, IUserNotifyTestPhoneNumberRequest, IThunkAPI>(
  'agent/profile/notification/test/sms',
  async (payload, thunkAPI) => {
    try {
      const response = await api.testNotifySms(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const testNotifyVoice = createAsyncThunk<unknown, IUserNotifyTestPhoneNumberRequest, IThunkAPI>(
  'agent/profile/notification/test/voice',
  async (payload, thunkAPI) => {
    try {
      const response = await api.testNotifyVoice(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getUserDeclinedApplications = createAsyncThunk<number[], number, IThunkAPI>(
  'agent/profile/declinedapplication/list',
  async (userId, thunkAPI) => {
    try {
      const response = await api.getUserDeclinedApplicationsList(userId);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const checkCodeVerification = createAsyncThunk<unknown, ICodeRequest, IThunkAPI>(
  'agent/profile/code/check',
  async (payload, thunkAPI) => {
    try {
      const response = await api.checkVerificationCode(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const sendContactServiceDesk = createAsyncThunk<unknown, IHelpRequest, IThunkAPI>(
  'agent/profile/send/contactservicedesk',
  async (payload, thunkAPI) => {
    try {
      const response = await api.sendContactServiceDesk(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const userPhoneNumberVerification = createAsyncThunk<unknown, IMobileRequest, IThunkAPI>(
  'agent/profile/phone/verification',
  async (payload, thunkAPI) => {
    try {
      const response = await api.mobilePhoneVerification(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const resendPhoneVerification = createAsyncThunk<unknown, IMobileRequest, IThunkAPI>(
  'agent/profile/code/resend',
  async (payload, thunkAPI) => {
    try {
      const response = await api.resendPhoneVerification(payload);
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const canshowreferralmenu = createAsyncThunk<boolean, void, IThunkAPI>(
  'user/referral/menu',
  async (_, thunkAPI) => {
    try {
      const response = await api.canShowReferralMenu();
      return response.data || false;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearProfile(state) {
      state.profile = null;
      state.shippings = [];
      state.credentials = [];
    },
    clearState(state) {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
    },
    updateDeclinedMsg(state, action) {
      state.declinedMsg = action?.payload?.msg || '';
      state.declinedMsgHeading = action?.payload?.heading || '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.profile = action.payload;
      })
      .addCase(getUserShippings.fulfilled, (state, action) => {
        state.shippings = action.payload;
      })
      .addCase(getTimeZoneNames.fulfilled, (state, action) => {
        state.timezonenames = action.payload;
      })
      .addCase(getUserCredentials.fulfilled, (state, action) => {
        state.credentials = action.payload;
      })
      .addCase(setUserIdentification.fulfilled, (state, action) => {
        state.profile = action.payload;
      })
      .addCase(setUserProfileShippingAddress.fulfilled, (state, action) => {
        state.profile = action.payload as IUserProfile;
      })
      .addCase(setUserPassword.fulfilled, (state, action) => {
        state.profile = action.payload;
      })
      .addCase(setUserNotify.fulfilled, (state, action) => {
        state.profile = action.payload;
      })
      .addCase(getUserDeclinedApplications.fulfilled, (state, action) => {
        state.declinedApplications = action.payload;
      })
      .addMatcher(isFulfilledAction, state => {
        state.isSuccess = true;
        state.isError = false;
        state.isFetching = false;
      })
      .addMatcher(isRejectedAction, state => {
        state.isError = true;
        state.isSuccess = false;
        state.isFetching = false;
      })
      .addMatcher(isPendingAction, state => {
        state.isFetching = true;
      });
  },
});

// actions
export const { clearState, clearProfile, updateDeclinedMsg } = slice.actions;

export const getDeclinedApplicationsList = (state: RootState): number[] => state.user.declinedApplications;

export const getDeclinedApplicationMsg = (state: RootState): string => state.user.declinedMsg;

export const getDeclinedApplicationMsgHeading = (state: RootState): string => state.user.declinedMsgHeading;

export default slice.reducer;
